/**
 * ExpressionEditor
 * Configuration editor for the `expression` control (Plan/05 §4, "Option B").
 *
 * Exposes exactly the two design-time options `ExpressionControl` reads out of `control.config` —
 * `syntax` and `outputFormat`. Nothing else belongs here: the wildcard catalog the field edits
 * against is a **live** `ExpressionsApiClient`, which is not serializable and therefore cannot live
 * in a form schema at all; a host supplies it once at runtime via `configureExpressionControl()`
 * from `@atlas-forms/controls-advanced-react`.
 *
 * ── Why this file is now four lines of JSX ─────────────────────────────────────────────────────
 *
 * It used to define its own syntax-option list, its own output-format-option list and its own
 * select/help-text markup — all near-identical to `ExpressionBuilderSection`'s, which lives one
 * directory up **in this same package** and is already exported from `EditorImplementations/index.ts`.
 * Two property editors reimplementing the same two `<select>`s with no package boundary between
 * them is duplication with nothing to justify it, and the two lists had already begun to drift (the
 * output-format options were ordered differently and hardcoded a different "(default)" marker).
 *
 * So this now renders the shared section, configured for Option B's two differences:
 *
 *   - `configKey={null}` — Option B stores `syntax`/`outputFormat` **flat** on `control.config`,
 *     where §3B/§3C nest them under `config.expressionBuilder`. That control owns its whole config
 *     bag and has no third key to disambiguate, so there is nothing for flat keys to collide with.
 *   - `showEnabledToggle={false}` — there is no `enabled` flag, because a control of type
 *     `expression` *is* the opt-in. A toggle would be a switch that does nothing.
 *
 * `defaultOutputFormat` is imported rather than written as a literal, so the "(default)" marker
 * shown here cannot disagree with what `ExpressionControl` actually falls back to.
 * `EXPRESSION_CONTROL_DEFAULT_FORMAT` is that control's own constant, from
 * `@atlas-forms/controls-advanced-react` — a package this one already depends on
 * (`ControlCanvas.tsx` imports from it today). It used to be a `'html'` literal restated here on the
 * reasoning that importing would drag in `@bizfirst/expressions-types`; it does not, because the
 * constant is declared in `expressionControlRuntime`, whose only import of the Expressions chain is
 * `import type`. The delimiter default is *not* imported alongside it — `ExpressionBuilderSection`
 * already owns that fallback for every editor that uses it.
 */

import React from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';
import { ExpressionBuilderSection } from '../ExpressionBuilderSection';
import { EXPRESSION_CONTROL_DEFAULT_FORMAT } from '@atlas-forms/controls-advanced-react';

function renderExpressionEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;

  return (
    <ExpressionBuilderSection
      control={control}
      onConfigChange={onConfigChange}
      title="Expression Builder Configuration"
      configKey={null}
      showEnabledToggle={false}
      defaultOutputFormat={EXPRESSION_CONTROL_DEFAULT_FORMAT}
      outputFormatHelp="Drives both the editor and the read-only preview badge on the field."
      hostConfigHelp={
        <p className="af-properties-tab__help">
          The wildcard catalog this field browses is supplied at runtime by the hosting application
          (<code>configureExpressionControl()</code>), not by the form schema — a live API client is
          not serializable. Until a host configures it, the field renders an explanatory notice
          instead of an editor.
        </p>
      }
    />
  );
}

export const ExpressionEditor: PropertyEditor = {
  type: 'expression',
  label: 'Expression Builder',
  description: 'Configure delimiter syntax and output format for an Expression Builder field',
  render: renderExpressionEditor,
};
